import React, { createContext, Dispatch, ReactNode, SetStateAction, useContext, useMemo } from 'react';
import useGroupSettingsEffects from '../../hooks/context/effects/useGroupSettingsEffects';
import useGroupSettingsContext from '../../hooks/context/useGroupSettingsContext';
import { DashboardColumn, InvestmentThesis } from '../../types/files';
import { AuthContext } from '../AuthContext';
import storage from '../../utils/storage';
import { CustomField, EmailFolder, TagGroup } from '../../types/common';

type GroupSettingsContextProps = {
  settingsLoaded: boolean;
  group: string;
  defaultStatus: string;
  setDefaultStatus: Dispatch<SetStateAction<string>>;
  statuses: string[];
  setStatuses: Dispatch<SetStateAction<string[]>>;
  tags: string[];
  setTags: Dispatch<SetStateAction<string[]>>;
  keyPeopleTags: string[];
  setKeyPeopleTags: Dispatch<SetStateAction<string[]>>;
  tagGroups: TagGroup[];
  setTagGroups: Dispatch<SetStateAction<TagGroup[]>>;
  customFields: CustomField[];
  setCustomFields: Dispatch<SetStateAction<CustomField[]>>;
  stages: string[];
  setStages: Dispatch<SetStateAction<string[]>>;
  externalSync: boolean;
  setExternalSync: Dispatch<SetStateAction<boolean>>;
  autoIngestFromEmail: boolean;
  setAutoIngestFromEmail: Dispatch<SetStateAction<boolean>>;
  autoSendDigestEmail: boolean;
  setAutoSendDigestEmail: Dispatch<SetStateAction<boolean>>;
  autoSendIngestionNotifications: boolean;
  setAutoSendIngestionNotification: Dispatch<SetStateAction<boolean>>;
  investmentThesis?: InvestmentThesis | null;
  setInvestmentThesis: Dispatch<SetStateAction<InvestmentThesis | any>>;
  excludedCompanyTerms: string[];
  setExcludedCompanyTerms: Dispatch<SetStateAction<string[]>>;
  dashboardColumns: DashboardColumn[];
  setDashboardColumns: Dispatch<SetStateAction<DashboardColumn[]>>;
  emailFolders: EmailFolder[];
  setEmailFolders: Dispatch<SetStateAction<EmailFolder[]>>;
  connectedApps: string[];
  setConnectedApps: Dispatch<SetStateAction<string[]>>;
  refreshSettings: () => void;
};

export const GroupSettingsContext = createContext<GroupSettingsContextProps>({
  settingsLoaded: false,
  group: '',
  defaultStatus: '',
  setDefaultStatus: () => { },
  statuses: [],
  setStatuses: () => { },
  tags: [],
  setTags: () => { },
  keyPeopleTags: [],
  setKeyPeopleTags: () => { },
  tagGroups: [],
  setTagGroups: () => { },
  customFields: [],
  setCustomFields: () => { },
  stages: [],
  setStages: () => { },
  externalSync: false,
  setExternalSync: () => { },
  autoIngestFromEmail: false,
  setAutoIngestFromEmail: () => { },
  autoSendDigestEmail: false,
  setAutoSendDigestEmail: () => { },
  autoSendIngestionNotifications: false,
  setAutoSendIngestionNotification: () => { },
  investmentThesis: null,
  setInvestmentThesis: () => { },
  excludedCompanyTerms: [],
  setExcludedCompanyTerms: () => { },
  dashboardColumns: [],
  setDashboardColumns: () => { },
  emailFolders: [],
  setEmailFolders: () => { },
  connectedApps: [],
  setConnectedApps: () => { },
  refreshSettings: () => { },
});

const GroupSettingsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { userGroup } = useContext(AuthContext);
  const {
    settingsLoaded,
    group,
    defaultStatus,
    statuses,
    tags,
    keyPeopleTags,
    tagGroups,
    customFields,
    stages,
    externalSync,
    autoIngestFromEmail,
    autoSendDigestEmail,
    autoSendIngestionNotifications,
    investmentThesis,
    excludedCompanyTerms,
    dashboardColumns,
    emailFolders,
    connectedApps,
    setDefaultStatus,
    setStatuses,
    setTags,
    setKeyPeopleTags,
    setTagGroups,
    setCustomFields,
    setStages,
    setExternalSync,
    setAutoIngestFromEmail,
    setAutoSendDigestEmail,
    setAutoSendIngestionNotification,
    setInvestmentThesis,
    setExcludedCompanyTerms,
    setDashboardColumns,
    setEmailFolders,
    setConnectedApps,
  } = useGroupSettingsContext();
  const { refreshSettings } = useGroupSettingsEffects(userGroup);

  const contextValue = useMemo(() => ({
    settingsLoaded,
    group,
    defaultStatus,
    setDefaultStatus,
    statuses,
    setStatuses,
    tags,
    setTags,
    tagGroups,
    setTagGroups,
    keyPeopleTags,
    setKeyPeopleTags,
    customFields,
    setCustomFields,
    stages,
    setStages,
    externalSync,
    setExternalSync,
    autoIngestFromEmail,
    setAutoIngestFromEmail,
    autoSendDigestEmail,
    setAutoSendDigestEmail,
    autoSendIngestionNotifications,
    setAutoSendIngestionNotification,
    investmentThesis,
    setInvestmentThesis,
    excludedCompanyTerms,
    setExcludedCompanyTerms,
    dashboardColumns,
    setDashboardColumns,
    emailFolders,
    setEmailFolders,
    connectedApps,
    setConnectedApps,
    refreshSettings
  }), [
    settingsLoaded,
    group,
    defaultStatus,
    setDefaultStatus,
    statuses,
    setStatuses,
    tags,
    setTags,
    tagGroups,
    setTagGroups,
    keyPeopleTags,
    setKeyPeopleTags,
    customFields,
    setCustomFields,
    stages,
    setStages,
    externalSync,
    setExternalSync,
    autoIngestFromEmail,
    setAutoIngestFromEmail,
    autoSendDigestEmail,
    setAutoSendDigestEmail,
    investmentThesis,
    autoSendIngestionNotifications,
    setAutoSendIngestionNotification,
    setInvestmentThesis,
    excludedCompanyTerms,
    setExcludedCompanyTerms,
    dashboardColumns,
    setDashboardColumns,
    emailFolders,
    setEmailFolders,
    connectedApps,
    setConnectedApps,
    refreshSettings,
  ]);

  if (storage.getItem('config.logger-context'))
    console.log("CTX", ["GroupSettings", contextValue]);

  return (
    <GroupSettingsContext.Provider value={contextValue}>
      {children}
    </GroupSettingsContext.Provider>
  );
};

export default GroupSettingsProvider;
